import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { profileAction, updateAction } from "@/redux/actions";
import history from "@/config/history";
import { profileConstant } from "@/redux/constants";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown02Icon,
  ArrowMoveDownLeftIcon,
  ArrowUp02Icon,
  SearchIcon,
} from "@hugeicons/core-free-icons";
import { Kbd } from "@/components/ui/kbd";
import { PillToggles } from "@/components/ui/pill-toggles";
import { JobsOption } from "./search-options/jobs-option";
import { TasksOption } from "./search-options/tasks-option";
import { PagesOption } from "./search-options/pages-option";
import { NullOption } from "./search-options/null-option";
import { DefaultOption } from "./search-options/default-option";
import type { SearchOption } from "./search-options/types";
import { InboxOption } from "./search-options/inbox-option";

const SearchDialog = ({
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const dispatch = useDispatch();
  const { global } = useSelector((s: any) => s.profile);
  const { globalLoading } = useSelector((s: any) => s.UI);

  const [value, setValue] = useState("");
  const [hideNoOption, setHideNoOption] = useState(true);
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const typeOptions = [
    { value: "leads", label: "Leads" },
    { value: "tasks", label: "Tasks" },
    { value: "pages", label: "Pages" },
    { value: "contacts", label: "Contacts" },
  ];

  // Use controlled or internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  // Update search with debounce
  useEffect(() => {
    const delaySet = setTimeout(() => {
      if (value) {
        const params: { search: string; types?: string[] } = { search: value };
        if (selectedTypes.length) params.types = selectedTypes;
        dispatch(
          profileAction.getGlobalData(params, () => {
            setHideNoOption(false);
          }) as any
        );
      }
    }, 100);
    return () => clearTimeout(delaySet);
  }, [value, selectedTypes, dispatch]);

  useEffect(() => {
    if (!value || (value && globalLoading)) {
      setHideNoOption(true);
    }
  }, [value, globalLoading, global]);

  // Focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
    setValue("");
    setSelectedIndex(-1);
    setSelectedTypes([]);
  }, [open]);

  const handleClick = (newValue: SearchOption | null | undefined) => {
    if (!newValue) return;
    const { value: itemValue, type } = newValue;

    if (type === "job_user") {
      history.push("/teams");
      dispatch(
        updateAction.update(profileConstant.GET_GLOBAL_S, { data: [] }) as any
      );
      dispatch(
        updateAction.update(
          profileConstant.SET_SEARCH_TITLE,
          newValue.title
        ) as any
      );
    } else if (type === "client_user") {
      history.push("/teams/client");
      dispatch(
        updateAction.update(profileConstant.GET_GLOBAL_S, { data: [] }) as any
      );
      dispatch(
        updateAction.update(
          profileConstant.SET_SEARCH_TITLE,
          newValue.title
        ) as any
      );
    } else if (type === "jobs") {
      history.push(`/leads/detail/${itemValue}`);
      dispatch(
        updateAction.update(profileConstant.GET_GLOBAL_S, { data: [] }) as any
      );
    } else if (type === "groups") {
      history.push(`/chat/teams?id=${itemValue}`);
      dispatch(
        updateAction.update(profileConstant.GET_GLOBAL_S, { data: [] }) as any
      );
    } else if (type === "tasks") {
      history.push(`/tasks/${itemValue}`);
      dispatch(
        updateAction.update(profileConstant.GET_GLOBAL_S, { data: [] }) as any
      );
    } else if (type === "pages") {
      history.push(`/leads/detail/${newValue.lead_id}/pages/${newValue.value}`);
      dispatch(
        updateAction.update(profileConstant.GET_GLOBAL_S, { data: [] }) as any
      );
    } else if (type === "inbox") {
      history.push(`/leads/detail/${newValue.lead_id}`);
      dispatch(
        updateAction.update(profileConstant.GET_GLOBAL_S, { data: [] }) as any
      );
    }

    setValue("");
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const options: SearchOption[] = (global as SearchOption[]) || [];
    const filteredOptions = hideNoOption ? [] : options;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => {
        const newIndex = prev < filteredOptions.length - 1 ? prev + 1 : prev;
        // Scroll selected item into view
        setTimeout(() => {
          const element = listRef.current?.children[newIndex] as HTMLElement;
          element?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }, 0);
        return newIndex;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => {
        const newIndex = prev > 0 ? prev - 1 : -1;
        // Scroll selected item into view
        if (newIndex >= 0) {
          setTimeout(() => {
            const element = listRef.current?.children[newIndex] as HTMLElement;
            element?.scrollIntoView({ block: "nearest", behavior: "smooth" });
          }, 0);
        }
        return newIndex;
      });
    } else if (e.key === "Enter" && selectedIndex >= 0) {
      e.preventDefault();
      handleClick(filteredOptions[selectedIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    setHideNoOption(true);
    setSelectedIndex(-1);
  };

  const options: SearchOption[] = (global as SearchOption[]) || [];
  const showOptions = !hideNoOption && value && options.length > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="max-w-md p-0 overflow-hidden"
        showCloseButton={false}
      >
        <div className="relative">
          <div className="relative">
            <HugeiconsIcon
              icon={SearchIcon}
              size={16}
              className="absolute left-3.5 top-1/2 -translate-y-1/2"
            />
            <Input
              ref={inputRef}
              value={value}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Search for a lead, block, contact or task"
              className="w-full pr-8 pl-9 h-12 rounded-b-none border-none outline-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>

          <div className="border-t px-3 py-2">
            <PillToggles
              value={selectedTypes}
              onChange={(next) => {
                setSelectedTypes(next);
                setSelectedIndex(-1);
              }}
              options={typeOptions}
            />
          </div>

          {showOptions && (
            <div
              ref={listRef}
              className="max-h-[300px] overflow-y-auto border-t bg-background"
            >
              {options.map((option: SearchOption, index: number) => {
                const isSelected = index === selectedIndex;
                const commonProps = {
                  key: `${option.type}-${option.value}-${index}`,
                  option,
                  isSelected,
                  onClick: () => handleClick(option),
                };

                switch (option.type) {
                  case "jobs":
                    return <JobsOption {...commonProps} />;
                  case "inbox": 
                    return <InboxOption {...commonProps} />;
                  case "tasks":
                    return <TasksOption {...commonProps} />;
                  case "pages":
                    return <PagesOption {...commonProps} />;
                  case "null":
                    return <NullOption {...commonProps} />;
                  default:
                    return <DefaultOption {...commonProps} />;
                }
              })}
            </div>
          )}

          {value && !showOptions && !globalLoading && (
            <div className="border-t py-12 text-sm text-muted-foreground text-center">
              Results not found
            </div>
          )}
          <div className="border-t p-1 py-2">
            <div className="flex items-center gap-1">
              <Kbd>
                <HugeiconsIcon icon={ArrowUp02Icon} size={16} />
              </Kbd>
              <Kbd>
                <HugeiconsIcon icon={ArrowDown02Icon} size={16} />
              </Kbd>
              <span className="ml-[2px] font-semibold text-xs">Move</span>
              <div className="flex items-center gap-1 ml-1">
                <Kbd>
                  <HugeiconsIcon icon={ArrowMoveDownLeftIcon} size={16} />
                </Kbd>
                <span className="ml-[2px] font-semibold text-xs">Enter</span>
              </div>
              <div className="ml-auto">
                <Kbd>ESC</Kbd>
                <span className="ml-[4px] font-semibold text-xs">Clear</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { SearchDialog };
